import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import {
  createAuthBaseQuery,
  setAuthTokenToCookies,
  removeAuthTokenFromCookies,
  syncSessionBaseline,
} from "../../common/utils";
import { clearActAs } from "../../utils/actAs";
import { clearDemoMode } from "../../utils/demoMode";
import { HOSTNAME } from "../../constant/host";

/**
 * このブラウザのサインイン状態を破棄する（クライアント側）。
 *
 * BE の `POST /api/logout` が Django セッションを落とすのと対で、FE 側の
 * 認証・代理操作・デモの痕跡をまとめて消す。どれかが残ると「Cookie は
 * 差し替わったのにデモガードだけ効く」「代理操作ヘッダーが付いたまま」
 * といった食い違いになるため、必ずこの関数を通して掃除する。
 *
 * @param {import("@reduxjs/toolkit").Dispatch} dispatch
 */
const clearClientSession = (dispatch) => {
  removeAuthTokenFromCookies();
  clearDemoMode();
  clearActAs();
  dispatch(userApi.util.resetApiState());
  // 意図した切り替えなので SessionSwitchGuard を発火させない
  syncSessionBaseline();
};

export const signinApi = createApi({
  reducerPath: "signinApi",
  baseQuery: fetchBaseQuery({
    baseUrl: HOSTNAME,
    credentials: "include",
    redirect: "manual",
  }),
  endpoints: (builder) => ({
    signinUser: builder.mutation({
      query: (credentials) => {
        return {
          url: "/api/login",
          method: "POST",
          body: {
            username: credentials.username,
            password: credentials.password,
            remember_me: credentials.rememberMe,
          },
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const token = data.token;
          if (token) {
            // デモ閲覧・代理操作の名残があれば破棄する（残っていると本人の
            // 書き込みがデモガードで誤って遮断されたり、代理操作ヘッダーが
            // 付いたままになる）。setAuthTokenToCookies より前に消して
            // おくことで、Cookie 更新時のセッション指紋が最新になる。
            clearDemoMode();
            clearActAs();
            setAuthTokenToCookies(token);
            // ユーザー情報のキャッシュを無効化して再取得をトリガー
            dispatch(userApi.util.invalidateTags(['CurrentUser']));
          }
        } catch {
          // console.error("Login failed:", error);
        }
      },
    }),
    /**
     * サインアウト。
     *
     * BE のセッション破棄が失敗しても、クライアント側の掃除だけは必ず行う
     * （通信エラーでサインアウトできない状態に閉じ込めないため）。
     *
     * @param {{ redirectTo?: string | null } | void} arg
     *   `redirectTo` に遷移先を渡すと、掃除後にフルリロード遷移する。
     *   既定は `/signin`。`null` を渡すと遷移せず、呼び出し側が続きの処理
     *   （デモリンクへの入場など）を行える。
     */
    signoutUser: builder.mutation({
      query: () => ({
        url: "/api/logout",
        method: "POST",
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        const redirectTo = arg?.redirectTo === undefined ? "/signin" : arg.redirectTo;
        try {
          await queryFulfilled;
        } catch (error) {
          console.error("Logout failed:", error);
        } finally {
          clearClientSession(dispatch);
          if (redirectTo) {
            window.location.replace(redirectTo);
          }
        }
      },
    }),
  }),
});

export const userApi = createApi({
  reducerPath: "userApi",
  // 認証・CSRF・act-as ヘッダー付きの共通 baseQuery。
  // デモモード中の書き込み遮断（/api/me の PATCH、ブックマークの
  // POST/DELETE 等）もここで一括して効く。
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["CurrentUser"],
  endpoints: (builder) => ({
    getCurrentUser: builder.query({
      query: () => ({
        url: "/api/me",
        method: "GET",
      }),
      providesTags: ["CurrentUser"],
    }),
    updateCurrentUser: builder.mutation({
      query: (userData) => ({
        url: "/api/me",
        method: "PATCH",
        body: userData,
      }),
      async onQueryStarted(userData, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          if (userData instanceof FormData) {
            // FormData は Object.assign でマージできないため、キャッシュを再取得
            dispatch(userApi.util.invalidateTags(['CurrentUser']));
          } else {
            const patch = { ...userData };
            // パスワードは changePassword（POST /api/me/password）専用。
            // 万一渡されてもキャッシュに平文を残さない。
            delete patch.password;
            delete patch.current_password;
            // BE は capacity_index の更新時に capacity_index_updated_at を
            // 自動打刻する（docs/api/me.md）。キャッシュ側も同期させないと
            // isCapacityFresh() が古い時刻で false を返し、忙しさメーターの
            // 表示が更新されない。
            if (patch.capacity_index !== undefined) {
              patch.capacity_index_updated_at = new Date().toISOString();
            }
            dispatch(
              userApi.util.updateQueryData('getCurrentUser', undefined, (draft) => {
                Object.assign(draft.data, patch);
              })
            );
          }
        } catch {
          // エラーの場合は何もしない（既存のキャッシュを保持）
        }
      },
    }),
    /**
     * パスワード変更（POST /api/me/password）。
     *
     * BE は現在パスワードの照合と Django パスワードバリデーションを行い、
     * 成功時に DRF Token を再発行する（旧トークンは無効化）。返ってきた
     * 新トークンを Cookie に保存し直すことで現端末のログインを維持する。
     *
     * @param {{ current_password: string, password: string, password_confirm: string }} body
     */
    changePassword: builder.mutation({
      query: (body) => ({
        url: "/api/me/password",
        method: "POST",
        body,
      }),
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.token) {
            setAuthTokenToCookies(data.token);
          }
        } catch {
          // エラー表示は呼び出し側（EmailPassword）で行う
        }
      },
    }),
    addShotBookmark: builder.mutation({
      query: (shot_id) => ({
        url: "/api/me/bookmarks/shots",
        method: "POST",
        body: { shot_id },
      }),
      async onQueryStarted(shot_id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          userApi.util.updateQueryData('getCurrentUser', undefined, (draft) => {
            if (!draft.data.bookmarked_shot_ids) {
              draft.data.bookmarked_shot_ids = [];
            }
            if (!draft.data.bookmarked_shot_ids.includes(shot_id)) {
              draft.data.bookmarked_shot_ids.push(shot_id);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    removeShotBookmark: builder.mutation({
      query: (shot_id) => ({
        url: "/api/me/bookmarks/shots",
        method: "DELETE",
        body: { shot_id },
      }),
      async onQueryStarted(shot_id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          userApi.util.updateQueryData('getCurrentUser', undefined, (draft) => {
            if (draft.data.bookmarked_shot_ids) {
              draft.data.bookmarked_shot_ids = draft.data.bookmarked_shot_ids.filter(
                (id) => id !== shot_id
              );
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});


// RTK Query で生成されたフックをまとめてエクスポート
export const { useSigninUserMutation, useSignoutUserMutation } = signinApi;
export const {
  useGetCurrentUserQuery,
  useUpdateCurrentUserMutation,
  useChangePasswordMutation,
  useAddShotBookmarkMutation,
  useRemoveShotBookmarkMutation,
} = userApi;
